import { EmeraldInputV2 } from "@emerald-react/input-v2";
import React, { useEffect, useId, useRef, useState } from "react";

interface CustomSliderProps {
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  showInput?: boolean;
  className?: string;
  label?: string;
  isInputOnLeft?: boolean;
}

/**
 * Custom Slider Component
 *
 * A reliable, lightweight slider component that properly handles:
 * - Focus management (releases focus after dragging)
 * - Input field synchronization
 * - Touch and mouse events
 * - Keyboard navigation
 *
 * @param min - Minimum value
 * @param max - Maximum value
 * @param step - Step increment
 * @param value - Current value
 * @param onChange - Callback when value changes
 * @param showInput - Show input field for manual entry
 * @param className - Additional CSS classes
 * @param label - Optional label text
 */
const CustomSlider = ({
  min,
  max,
  step,
  value,
  onChange,
  showInput = true,
  className = "",
  label,
  isInputOnLeft = false,
}: CustomSliderProps) => {
  const [inputValue, setInputValue] = useState(String(value));
  const sliderRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const baseId = useId();
  const sliderId = `${baseId}-slider`;
  const inputId = `${baseId}-input`;

  // Sync input value when slider value changes
  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  // Calculate percentage for visual progress
  const percentage = ((value - min) / (max - min)) * 100;

  // Handle slider change
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    onChange(newValue);
  };

  // Handle input change
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setInputValue(e.target.value);
  };

  // Handle input blur - validate and update
  const handleInputBlur = () => {
    let newValue = Number(inputValue);
    if (Number.isNaN(newValue)) {
      newValue = value;
    }
    newValue = Math.max(min, Math.min(max, newValue));
    onChange(newValue);
    setInputValue(String(newValue));
  };

  // Handle input key down
  const handleInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === "Enter") {
      handleInputBlur();
    }
  };

  // Handle drag end - release focus
  const handleDragEnd = () => {
    if (sliderRef.current) {
      sliderRef.current.blur();
    }
  };

  // Handle mouse up on slider
  const handleMouseUp = () => {
    handleDragEnd();
  };

  // Handle touch end on slider
  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Handle mouse down
  const handleMouseDown = () => {
    // Placeholder for any drag start logic
  };

  // Handle touch start
  const handleTouchStart = () => {
    // Placeholder for any touch start logic
  };

  return (
    <div
      className={`flex flex-col gap-2 w-full ${className}`}
      ref={containerRef}
    >
      {label && (
        <label htmlFor={sliderId} className="text-sm font-medium text-data">
          {label}
        </label>
      )}

      <div className="flex gap-3 items-center">
        {/* Slider Track */}
        {showInput && isInputOnLeft && (
          <EmeraldInputV2
            id={inputId}
            type="number"
            min={min}
            max={max}
            step={step}
            size="small"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            aria-label="Value input"
          />
        )}
        <div className="relative flex-1">
          <input
            ref={sliderRef}
            id={sliderId}
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={handleSliderChange}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className="w-full appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, #767676 0%, #767676 ${percentage}%, #d3d3d3 ${percentage}%, #d3d3d3 100%)`,
            }}
          />
          <style>{`
            .slider {
              -webkit-appearance: none;
              width: 100%;
              height: 6px;
              border-radius: 3px;
              outline: none;
              padding: 0;
              margin: 0;
            }

            .slider::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 20px;
              height: 20px;
              border-radius: 50%;
              background: #767676;
              cursor: pointer;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
              transition: all 0.2s ease;
              border: none;
            }

            .slider::-webkit-slider-thumb:hover {
              background: #565656;
              box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
              transform: scale(1.1);
            }

            .slider::-webkit-slider-thumb:active {
              background: #565656;
              transform: scale(1.15);
            }

            .slider::-moz-range-thumb {
              width: 20px;
              height: 20px;
              border-radius: 50%;
              background: #767676;
              cursor: pointer;
              border: none;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
              transition: all 0.2s ease;
            }

            .slider::-moz-range-thumb:hover {
              background: #565656;
              box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
              transform: scale(1.1);
            }

            .slider::-moz-range-thumb:active {
              background: #565656;
              transform: scale(1.15);
            }

            .slider::-moz-range-track {
              background: transparent;
              border: none;
            }

            .slider::-moz-range-progress {
              background: #767676;
              height: 6px;
              border-radius: 3px;
            }
          `}</style>
        </div>

        {/* Value Display or Input */}
        {showInput && !isInputOnLeft && (
          <EmeraldInputV2
            id={inputId}
            type="number"
            min={min}
            max={max}
            step={step}
            size="small"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            aria-label="Value input"
          />
        )}
      </div>
    </div>
  );
};

export default CustomSlider;
